import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { Star, Send, Heart, Mail } from "lucide-react";
import api from "@/lib/api";

const cardColors = ["bg-sun", "bg-sky", "bg-bubble/40", "bg-tangerine/40", "bg-grape/40"];
const MAX_WORDS = 150;
const countWords = (s) => (s.trim() ? s.trim().split(/\s+/).length : 0);

function StarRow({ value, onChange, size = "w-12 h-12" }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex justify-center gap-1.5" data-testid="review-stars">
      {[1, 2, 3, 4, 5].map((n) => {
        const on = n <= (hover || value);
        return (
          <button
            type="button"
            key={n}
            onClick={() => onChange(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
            data-testid={`star-${n}`}
            className="p-1 active:scale-90 transition-transform"
          >
            <Star className={`${size} transition-colors ${on ? "fill-sun text-ink" : "fill-transparent text-ink/25"}`} strokeWidth={2.4} />
          </button>
        );
      })}
    </div>
  );
}

function relDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
}

export default function TestimonialsPage() {
  const [items, setItems] = useState([]);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [overlay, setOverlay] = useState(false);       // thank-you overlay visible
  const [reviewId, setReviewId] = useState(null);
  const [email, setEmail] = useState("");
  const [emailStep, setEmailStep] = useState("ask");   // "ask" | "done"
  const [savingEmail, setSavingEmail] = useState(false);

  const load = () => api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const words = countWords(text);

  const onTextChange = (e) => {
    const v = e.target.value;
    // block typing past the word limit rather than truncating silently
    if (countWords(v) <= MAX_WORDS) setText(v);
    else {
      const clipped = v.trim().split(/\s+/).slice(0, MAX_WORDS).join(" ");
      setText(clipped);
    }
  };

  const fireConfetti = () => {
    const colors = ["#FFDE59", "#FF5C9E", "#5CC8FF", "#B08CFF", "#FF8A3D"];
    const fire = (x) => confetti({ particleCount: 70, spread: 75, startVelocity: 45, origin: { x, y: 0.4 }, colors });
    fire(0.2); setTimeout(() => fire(0.5), 140); setTimeout(() => fire(0.8), 280);
  };

  const submit = async () => {
    if (rating < 1) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/testimonials", { rating, text });
      setReviewId(data.id);
      setOverlay(true);
      setEmailStep("ask");
      fireConfetti();
      // reset the form + refresh public list (review is live immediately)
      setRating(0); setText("");
      load();
    } catch {
      toast.error("Something went wrong — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  const submitEmail = async () => {
    if (!email.trim()) { setEmailStep("done"); return; }
    setSavingEmail(true);
    try {
      await api.put(`/testimonials/${reviewId}/email`, { email: email.trim() });
      setEmailStep("done");
    } catch {
      toast.error("Couldn't save that email");
    } finally {
      setSavingEmail(false);
    }
  };

  const closeOverlay = () => { setOverlay(false); setEmail(""); setEmailStep("ask"); };

  return (
    <div className="max-w-3xl mx-auto px-5 py-10 sm:py-12" data-testid="testimonials-page">
      {/* Submission card */}
      <div className="brut-card p-6 sm:p-8 bg-cream-solid">
        <div className="text-center">
          <span className="font-accent text-2xl sm:text-3xl text-tangerine">your turn</span>
          <h1 className="font-display font-bold text-4xl sm:text-5xl mt-1">How did we do?</h1>
          <p className="text-ink/60 mt-2">Tap the stars to rate your caricature experience.</p>
        </div>

        <div className="my-7">
          <StarRow value={rating} onChange={setRating} />
        </div>

        <label className="font-display font-semibold text-sm" htmlFor="review-text">
          Tell us about your experience <span className="text-ink/50">(optional)</span>
        </label>
        <textarea
          id="review-text"
          value={text}
          onChange={onTextChange}
          rows={4}
          placeholder="What did you love? Anything we could do better?"
          className="brut-input w-full mt-1 mb-1 min-h-28 resize-none"
          data-testid="review-textarea"
        />
        <div className={`text-right text-xs font-bold mb-4 ${words >= MAX_WORDS ? "text-bubble" : "text-ink/50"}`} data-testid="word-counter">
          {words}/{MAX_WORDS} words
        </div>

        <button
          onClick={submit}
          disabled={rating < 1 || submitting}
          className={`brut-btn w-full text-lg ${rating < 1 || submitting ? "bg-ink/15 text-ink/40 cursor-not-allowed shadow-none" : "bg-bubble text-white"}`}
          data-testid="submit-review-btn"
        >
          {submitting ? "Sending…" : <>Submit Review <Send className="w-5 h-5" /></>}
        </button>
      </div>

      {/* Public reviews */}
      <div className="mt-12">
        <h2 className="font-display font-bold text-2xl sm:text-3xl mb-1">What others said</h2>
        <p className="text-ink/60 mb-6">Real feedback from real (well-drawn) people.</p>
        {items.length === 0 ? (
          <div className="brut-card p-6 text-center text-ink/60" data-testid="no-reviews">Be the first to leave a review!</div>
        ) : (
          <div className="columns-1 sm:columns-2 gap-5">
            {items.map((t, i) => (
              <motion.div key={t.id} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className={`mb-5 break-inside-avoid brut border-ink p-5 shadow-brut ${cardColors[i % cardColors.length]}`} data-testid={`review-card-${i}`}>
                <div className="flex gap-0.5 mb-2">
                  {[...Array(t.rating || 0)].map((_, j) => <Star key={j} className="w-4 h-4 fill-ink text-ink" />)}
                </div>
                {(t.text || t.message) && <p className="text-ink/90 font-medium">“{t.text || t.message}”</p>}
                <p className="text-ink/50 text-xs font-bold mt-3">{relDate(t.created_at)}</p>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Full-screen thank-you overlay */}
      <AnimatePresence>
        {overlay && (
          <motion.div
            className="fixed inset-0 z-50 grid place-items-center p-5 bg-ink/60 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="thankyou-overlay"
          >
            <motion.div
              initial={{ scale: 0.85, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 20 }}
              className="brut-card bg-cream-solid p-7 sm:p-9 w-full max-w-md text-center"
            >
              <span className="inline-grid place-items-center w-16 h-16 rounded-full bg-sun border-[3px] border-ink shadow-brut mb-4 animate-bounce">
                <Heart className="w-8 h-8 text-bubble fill-bubble" />
              </span>

              {emailStep === "ask" ? (
                <>
                  <h2 className="font-display font-bold text-3xl">Thank you! 🎉</h2>
                  <p className="text-ink/70 mt-2">
                    Want us to follow up if we ever feature your review? Leave your email <span className="text-ink/50">(optional)</span>.
                  </p>
                  <div className="mt-5 flex items-center gap-2 brut-input !p-0 overflow-hidden">
                    <span className="grid place-items-center pl-3 text-ink/50"><Mail className="w-5 h-5" /></span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      className="w-full bg-transparent py-3 pr-3 outline-none"
                      data-testid="overlay-email-input"
                    />
                  </div>
                  <div className="mt-5 flex flex-col gap-2">
                    <button onClick={submitEmail} disabled={savingEmail}
                      className="brut-btn bg-bubble text-white w-full" data-testid="overlay-submit-email">
                      {savingEmail ? "Saving…" : "Submit Email"}
                    </button>
                    <button onClick={() => setEmailStep("done")}
                      className="brut-btn bg-white w-full" data-testid="overlay-no-thanks">
                      No thanks, I'm done
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="font-display font-bold text-3xl">Thanks again! 💛</h2>
                  <p className="text-ink/70 mt-2">Your review is live. We appreciate you!</p>
                  <button onClick={closeOverlay} className="brut-btn bg-sun w-full mt-6" data-testid="overlay-close">
                    Done
                  </button>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
